import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

interface RiskThresholds {
  critical: number;
  high: number;
  moderate: number;
}

interface AuditRecord {
  id: number | string;
  uid?: string;
  user_name?: string;
  user_role?: string;
  action?: string;
  module?: string;
  description?: string;
  ip_address?: string;
  created_at?: string;
}

interface CommunityIntervention {
  municipality: string;
  total: number;
  categories: {
    category: string;
    count: number;
    percentage: number;
    recommendation: string;
    programType: string;
  }[];
}

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}`;
};

const getIncidentType = (report: any): string =>
  String(
    report.incident_type ??
      report.incidentType ??
      report.case_type ??
      report.type_of_incident ??
      report.incident_category ??
      ''
  ).trim();

const getReportDate = (report: any): string => {
  const value =
    report.submitted_at ??
    report.created_at ??
    report.report_date ??
    report.date_submitted ??
    report.incident_date ??
    report.createdAt ??
    report.date_created ??
    '';

  if (!value) return '';

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return '';

  return `${parsed.getFullYear()}-${String(
    parsed.getMonth() + 1
  ).padStart(2, '0')}`;
};

const getCommunityName = (report: any): string => {
  // Prefer a municipality field instead of grouping by full street address.
  const municipality = String(
    report.municipality ??
      report.city ??
      report.town ??
      report.service_municipality ??
      report.incident_municipality ??
      ''
  ).trim();

  if (municipality) return municipality;

  const address = String(
    report.location ??
      report.address ??
      report.incident_location ??
      ''
  ).trim();

  if (!address) return '';

  const parts = address
    .split(',')
    .map((part: string) => part.trim())
    .filter(Boolean);

  // Example: "Street, Paete, Laguna, Philippines" -> "Paete"
  if (parts.length >= 4) return parts[parts.length - 3];

  // Example: "Street, Paete, Laguna" -> "Paete"
  if (parts.length === 3) return parts[1];

  if (parts.length === 2) return parts[1];

  return parts[0];
};

const getInterventionForCategory = (rawCategory: string) => {
  const category = rawCategory.toLowerCase();

  if (category.includes('neglect')) {
    return {
      programType: 'Parenting and Caregiver Support',
      recommendation:
        'Conduct parenting and child-protection seminars, caregiver support sessions, and referrals for families needing assistance. Coordinate follow-up with authorized social-welfare professionals.',
    };
  }

  if (
    category.includes('sexual') ||
    category.includes('trafficking') ||
    category.includes('exploitation')
  ) {
    return {
      programType: 'Child Protection and Abuse Prevention',
      recommendation:
        'Coordinate confidential child-protection education, safe reporting pathways, and specialist support with authorized child-protection professionals. Protect the identity and privacy of affected children and families.',
    };
  }

  if (category.includes('physical')) {
    return {
      programType: 'Positive Parenting and Non-Violent Discipline',
      recommendation:
        'Offer positive-parenting sessions, non-violent discipline education, and appropriate referral pathways for families requiring social-work support.',
    };
  }

  if (category.includes('emotional')) {
    return {
      programType: 'Child Well-Being and Family Support',
      recommendation:
        'Provide caregiver education on emotional well-being, supportive communication, and referrals to qualified psychosocial services when needed.',
    };
  }

  if (
    category.includes('online') ||
    category.includes('cyber') ||
    category.includes('internet')
  ) {
    return {
      programType: 'Digital Safety and Online Child Protection',
      recommendation:
        'Run age-appropriate digital-safety education for children and caregivers. Include online grooming awareness, safe reporting procedures, privacy, and account-safety practices.',
    };
  }

  return {
    programType: 'Community Child-Protection Awareness',
    recommendation:
      'Conduct community child-protection awareness and prevention activities. Explain how to report concerns safely and coordinate appropriate referrals with the responsible social-welfare office.',
  };
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [legalDocuments, setLegalDocuments] = useState<any[]>([]);
  const [auditTrail, setAuditTrail] = useState<AuditRecord[]>([]);

  const [selectedMonth, setSelectedMonth] =
    useState(getCurrentMonth);

  const [loadingCases, setLoadingCases] = useState(true);
  const [casesError, setCasesError] = useState('');

  const [riskThresholds, setRiskThresholds] =
    useState<RiskThresholds>({
      critical: 90,
      high: 80,
      moderate: 75,
    });

  const [lastSync, setLastSync] = useState(
    new Date().toLocaleTimeString()
  );

  const visibleUsers = users;

  const getRisk = (report: any) => {
    let score = 70;

    const incidentType = getIncidentType(report).toLowerCase();

    const priorityLevel = String(
      report.priority_level ?? ''
    )
      .toLowerCase()
      .trim();

    const priorIncidents = String(
      report.prior_incidents ?? ''
    )
      .toLowerCase()
      .trim();

    if (
      incidentType.includes('sexual abuse') ||
      incidentType.includes('child trafficking') ||
      incidentType.includes('online sexual exploitation')
    ) {
      score = 95;
    } else if (incidentType.includes('physical abuse')) {
      score = 90;
    } else if (incidentType.includes('emotional abuse')) {
      score = 80;
    }

    if (priorIncidents === 'yes') score += 5;
    if (priorityLevel === 'urgent') score += 3;
    if (priorityLevel === 'emergency') score += 5;

    score = Math.min(score, 100);

    let level = 'low';

    if (score >= riskThresholds.critical) {
      level = 'critical';
    } else if (score >= riskThresholds.high) {
      level = 'high';
    } else if (score >= riskThresholds.moderate) {
      level = 'moderate';
    }

    return { score, level };
  };

  const getAuditStatus = (
    action = '',
    description = ''
  ) => {
    const text = `${action} ${description}`.toLowerCase();

    if (
      text.includes('failed') ||
      text.includes('denied') ||
      text.includes('error')
    ) {
      return 'failed';
    }

    if (
      text.includes('warning') ||
      text.includes('pending')
    ) {
      return 'warning';
    }

    return 'success';
  };

  const getCommunityInterventions = (
    reports: any[]
  ): CommunityIntervention[] => {
    const communityMap: Record<
      string,
      {
        name: string;
        total: number;
        categories: Record<string, number>;
      }
    > = {};

    reports.forEach((report) => {
      const municipality = getCommunityName(report);
      const incidentType = getIncidentType(report);

      if (!municipality || !incidentType) return;

      const communityKey = municipality.toLowerCase();

      if (!communityMap[communityKey]) {
        communityMap[communityKey] = {
          name: municipality,
          total: 0,
          categories: {},
        };
      }

      communityMap[communityKey].total += 1;

      const existingCategory = Object.keys(
        communityMap[communityKey].categories
      ).find(
        (category) =>
          category.toLowerCase() === incidentType.toLowerCase()
      );

      const categoryKey = existingCategory || incidentType;

      communityMap[communityKey].categories[categoryKey] =
        (communityMap[communityKey].categories[categoryKey] || 0) + 1;
    });

    return Object.values(communityMap)
      .map((community) => ({
        municipality: community.name,
        total: community.total,
        categories: Object.entries(community.categories)
          .map(([category, count]) => {
            const intervention =
              getInterventionForCategory(category);

            return {
              category,
              count,
              percentage: Math.round(
                (count / community.total) * 100
              ),
              programType: intervention.programType,
              recommendation: intervention.recommendation,
            };
          })
          .sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.total - a.total);
  };

  const monthSubmissions = submissions.filter(
    (report) => getReportDate(report) === selectedMonth
  );

  const submissionsWithoutDate = submissions.filter(
    (report) => !getReportDate(report)
  ).length;

  const reportsWithoutCommunityOrCategory =
    monthSubmissions.filter(
      (report) =>
        !getCommunityName(report) || !getIncidentType(report)
    ).length;

  const communityInterventions =
    getCommunityInterventions(monthSubmissions);

  const totalReportsAnalyzed = communityInterventions.reduce(
    (sum, community) => sum + community.total,
    0
  );

  useEffect(() => {
    let mounted = true;

    const loadDashboardData = async () => {
      // Load system risk configuration.
      try {
        const response = await fetch(
          `${API_BASE}/get_system_config.php`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (
          result.success &&
          Array.isArray(result.data)
        ) {
          const riskConfig = result.data.find(
            (config: any) =>
              config.config_key === 'risk_rules'
          );

          if (
            riskConfig?.config_value &&
            typeof riskConfig.config_value === 'object'
          ) {
            const config = riskConfig.config_value;

            const getThreshold = (
              value: any,
              fallback: number
            ) =>
              Number(
                value && typeof value === 'object'
                  ? value.minScore ?? fallback
                  : value ?? fallback
              );

            if (mounted) {
              setRiskThresholds({
                critical: getThreshold(config.critical, 90),
                high: getThreshold(config.high, 80),
                moderate: getThreshold(config.moderate, 75),
              });
            }
          }
        }
      } catch (error) {
        console.error(
          'Failed to load risk configuration:',
          error
        );
      }

      // Load users.
      try {
        const response = await fetch(
          `${API_BASE}/get_users.php`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (mounted) {
          setUsers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to load users:', error);

        if (mounted) setUsers([]);
      }

      // Load submitted reports.
      try {
        if (mounted) {
          setLoadingCases(true);
          setCasesError('');
        }

        const response = await fetch(
          `${API_BASE}/get_submitted_cases.php`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        );

        const responseText = await response.text();

        if (!response.ok) {
          throw new Error(
            `get_submitted_cases.php returned HTTP ${response.status}: ${responseText.slice(0, 300)}`
          );
        }

        let data: any;

        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error(
            `The cases API did not return valid JSON: ${responseText.slice(0, 300)}`
          );
        }

        // Accept common API response shapes.
        const caseRows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.cases)
          ? data.cases
          : Array.isArray(data?.submissions)
          ? data.submissions
          : Array.isArray(data?.reports)
          ? data.reports
          : null;

        if (!caseRows) {
          throw new Error(
            'The cases API response does not contain an array. Check whether it returns an array, { data: [] }, { cases: [] }, or { reports: [] }.'
          );
        }

        if (mounted) {
          setSubmissions(caseRows);
        }

        if (caseRows.length === 0) {
          console.warn(
            'get_submitted_cases.php returned an empty array. Verify the PHP SQL query and database.'
          );
        }
      } catch (error) {
        console.error(
          'Failed to load submitted cases:',
          error
        );

        if (mounted) {
          setSubmissions([]);
          setCasesError(
            error instanceof Error
              ? error.message
              : 'Unable to load submitted reports.'
          );
        }
      } finally {
        if (mounted) setLoadingCases(false);
      }

      // Load legal documents.
      try {
        const response = await fetch(
          `${API_BASE}/get_rag_documents.php`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (mounted) {
          setLegalDocuments(
            Array.isArray(data)
              ? data
              : Array.isArray(data?.data)
              ? data.data
              : []
          );
        }
      } catch (error) {
        console.error(
          'Failed to load legal documents:',
          error
        );

        if (mounted) setLegalDocuments([]);
      }

      // Load audit logs.
      try {
        const response = await fetch(
          `${API_BASE}/get_audit_logs.php`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (mounted) {
          setAuditTrail(
            result.success && Array.isArray(result.data)
              ? result.data
              : []
          );
        }
      } catch (error) {
        console.error(
          'Failed to load audit trail:',
          error
        );

        if (mounted) setAuditTrail([]);
      }
    };

    loadDashboardData();

    const interval = setInterval(() => {
      if (mounted) {
        setLastSync(new Date().toLocaleTimeString());
      }
    }, 5000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [user?.uid, user?.role]);

  const quickLinks = [
    {
      label: 'User Management',
      icon: 'ri-team-line',
      path: '/admin-user-management',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      desc: 'Manage users and victims',
    },
    {
      label: 'System Config',
      icon: 'ri-settings-3-line',
      path: '/admin-system-config',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      desc: 'Case categories, statuses, risk rules',
    },
    {
      label: 'Legal Knowledge',
      icon: 'ri-scales-3-line',
      path: '/admin-legal-knowledge',
      color: 'text-red-600',
      bg: 'bg-red-50',
      desc: 'Laws, guidelines, and policies',
    },
    {
      label: 'Notifications',
      icon: 'ri-notification-3-line',
      path: '/admin-notifications',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      desc: 'Notification and escalation rules',
    },
    {
      label: 'Audit Trail',
      icon: 'ri-file-list-3-line',
      path: '/admin-audit-trail',
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      desc: 'Full system activity log',
    },
    {
      label: 'Maintenance',
      icon: 'ri-tools-line',
      path: '/admin-maintenance',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      desc: 'System health and information',
    },
  ];

  const kpis = [
    {
      label: 'Total Users',
      value: visibleUsers.length,
      icon: 'ri-team-line',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      label: 'Legal Documents',
      value: legalDocuments.length,
      icon: 'ri-scales-3-line',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Critical Risks',
      value: submissions.filter(
        (report) => getRisk(report).level === 'critical'
      ).length,
      icon: 'ri-alarm-warning-line',
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ];

  const recentAudit = [...auditTrail]
    .filter((record) => record.created_at)
    .sort(
      (a, b) =>
        new Date(b.created_at || '').getTime() -
        new Date(a.created_at || '').getTime()
    )
    .slice(0, 5);

  return (
    <AppLayout
      title="Admin Control Panel"
      breadcrumb={['Admin', 'Control Panel']}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl w-fit">
          <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
          System Online · Last sync: {lastSync}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {kpis.map((item) => (
            <div
              key={item.label}
              className="bg-white rounded-xl border border-gray-100 p-5"
            >
              <div
                className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center mb-3`}
              >
                <i
                  className={`${item.icon} ${item.color} text-lg`}
                />
              </div>

              <p className="text-3xl font-bold text-gray-900">
                {item.value}
              </p>

              <p className="text-sm text-gray-500 mt-1">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Quick Access
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {quickLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="bg-white rounded-xl border border-gray-100 p-4 text-left hover:border-teal-200 hover:bg-teal-50/30 transition-all cursor-pointer group"
              >
                <div
                  className={`w-10 h-10 ${link.bg} rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}
                >
                  <i
                    className={`${link.icon} ${link.color} text-lg`}
                  />
                </div>

                <p className="text-sm font-semibold text-gray-800">
                  {link.label}
                </p>

                <p className="text-xs text-gray-400 mt-0.5">
                  {link.desc}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Community-level interventions with month filtering */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">
                Community-Level Interventions
              </h3>

              <p className="text-xs text-gray-400 mt-1">
                Community recommendations based on submitted reports for the selected month
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label
                htmlFor="community-month"
                className="text-xs font-medium text-gray-600"
              >
                Report month
              </label>

              <input
                id="community-month"
                type="month"
                value={selectedMonth}
                onChange={(event) =>
                  setSelectedMonth(event.target.value)
                }
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />

              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <i className="ri-community-line text-blue-600 text-lg" />
              </div>
            </div>
          </div>

          <div className="p-5">
            {loadingCases ? (
              <div className="text-center py-10">
                <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  Loading submitted reports...
                </p>
              </div>
            ) : casesError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-700">
                  Unable to load submitted reports
                </p>
                <p className="text-xs text-red-600 mt-2 break-words">
                  {casesError}
                </p>
                <p className="text-xs text-red-600 mt-2">
                  Check the get_submitted_cases.php endpoint and its database query.
                </p>
              </div>
            ) : communityInterventions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-bar-chart-line text-gray-400 text-xl" />
                </div>

                <p className="text-sm font-medium text-gray-600">
                  No community-level pattern available for {selectedMonth}.
                </p>

                <p className="text-xs text-gray-400 mt-2">
                  {submissions.length === 0
                    ? 'The cases API returned no reports. Check the PHP query and database connection.'
                    : 'Reports may belong to another month, or may be missing a date, community, or incident type.'}
                </p>

                {submissionsWithoutDate > 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    {submissionsWithoutDate} report(s) have no recognized date field.
                  </p>
                )}

                {reportsWithoutCommunityOrCategory > 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    {reportsWithoutCommunityOrCategory} report(s) in this month are missing a recognized community or incident category.
                  </p>
                )}

                {submissions.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Total reports loaded: {submissions.length}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 font-semibold">
                    {communityInterventions.length} communities
                  </span>

                  <span className="rounded-full bg-teal-50 text-teal-700 px-3 py-1 font-semibold">
                    {totalReportsAnalyzed} reports analyzed
                  </span>

                  <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1 font-semibold">
                    Month: {selectedMonth}
                  </span>

                  {submissionsWithoutDate > 0 && (
                    <span className="rounded-full bg-amber-50 text-amber-700 px-3 py-1">
                      {submissionsWithoutDate} reports missing dates
                    </span>
                  )}
                </div>

                {communityInterventions.map((community) => (
                  <section
                    key={community.municipality.toLowerCase()}
                    className="rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-map-pin-2-line text-teal-600" />

                        <h4 className="font-semibold text-gray-900">
                          {community.municipality}
                        </h4>
                      </div>

                      <span className="text-xs rounded-full bg-white border border-gray-200 px-3 py-1 text-gray-600">
                        {community.total} report
                        {community.total === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="p-4 space-y-3">
                      {community.categories.map((item) => (
                        <div
                          key={`${community.municipality}-${item.category}`}
                          className="rounded-lg border border-gray-100 p-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {item.category}
                              </p>

                              <p className="text-xs text-gray-500 mt-1">
                                {item.count} report
                                {item.count === 1 ? '' : 's'} ·{' '}
                                {item.percentage}% of reports in this community
                              </p>
                            </div>

                            <span className="text-xs rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 font-semibold">
                              {item.programType}
                            </span>
                          </div>

                          <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 p-3">
                            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                              Suggested intervention
                            </p>

                            <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                              {item.recommendation}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent activity and user breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Recent Activity
              </h3>

              <button
                onClick={() => navigate('/admin-audit-trail')}
                className="text-xs text-teal-600 hover:underline cursor-pointer"
              >
                View all
              </button>
            </div>

            <div className="divide-y divide-gray-50">
              {recentAudit.length > 0 ? (
                recentAudit.map((record) => {
                  const status = getAuditStatus(
                    record.action || '',
                    record.description || ''
                  );

                  return (
                    <div
                      key={record.id}
                      className="px-5 py-3 flex items-start gap-3"
                    >
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          status === 'success'
                            ? 'bg-green-100'
                            : status === 'failed'
                            ? 'bg-red-100'
                            : 'bg-amber-100'
                        }`}
                      >
                        <i
                          className={`text-xs ${
                            status === 'success'
                              ? 'ri-checkbox-circle-line text-green-600'
                              : status === 'failed'
                              ? 'ri-close-circle-line text-red-600'
                              : 'ri-error-warning-line text-amber-600'
                          }`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 font-medium truncate">
                          {record.action || 'Activity'}
                        </p>

                        <p className="text-xs text-gray-400 truncate">
                          {record.user_name || 'System User'}
                          {record.module ? ` · ${record.module}` : ''}
                        </p>

                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {record.description || 'No description'}
                        </p>

                        <p className="text-[11px] text-gray-300 mt-0.5">
                          {record.created_at}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  No recent audit activity.
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                User Breakdown
              </h3>

              <button
                onClick={() => navigate('/admin-user-management')}
                className="text-xs text-teal-600 hover:underline cursor-pointer"
              >
                Manage
              </button>
            </div>

            <div className="p-5 space-y-4">
              {[
                {
                  role: 'admin',
                  label: 'Administrators',
                  color: 'bg-red-500',
                  badge: 'bg-red-100 text-red-700',
                },
                {
                  role: 'supervisor',
                  label: 'Supervisors',
                  color: 'bg-blue-500',
                  badge: 'bg-blue-100 text-blue-700',
                },
                {
                  role: 'victim',
                  label: 'Victims',
                  color: 'bg-purple-500',
                  badge: 'bg-purple-100 text-purple-700',
                },
                {
                  role: 'social_worker',
                  label: 'Social Workers',
                  color: 'bg-teal-500',
                  badge: 'bg-teal-100 text-teal-700',
                },
              ].map((roleInfo) => {
                const count = visibleUsers.filter(
                  (item) => item.role === roleInfo.role
                ).length;

                const percentage =
                  visibleUsers.length > 0
                    ? Math.round(
                        (count / visibleUsers.length) * 100
                      )
                    : 0;

                return (
                  <div key={roleInfo.role}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleInfo.badge}`}
                      >
                        {roleInfo.label}
                      </span>

                      <span className="text-sm font-bold text-gray-800">
                        {count}
                      </span>
                    </div>

                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${roleInfo.color} transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}